import React from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import { ObjetoAvatar } from 'components'
import useStyles from './DashboardKpiCell.styles'

/**
 * Presentational component to render a single KPI cell card.
 * Encapsulates regional layouts, status badge grids and text descriptions.
 */
export const DashboardKpiCell = ({ cell, dataItem, isProcessing, onConfigClick }) => {
  const classes = useStyles()

  if (!cell) return null

  const isRegionalLayout = ['cadaveres_dia', 'exames_dia', 'vestigios_dia'].includes(cell.type)

  // Map readable title according to configured KPI type
  const getKpiTitle = () => {
    switch (cell.type) {
      case 'cadaveres_dia': return 'Cadáveres no Dia'
      case 'exames_dia': return 'Exames no Dia'
      case 'vestigios_dia': return 'Vestígios no Dia'
      case 'vestigios_setor': return cell.sectorName ? `Vestígios - ${cell.sectorName}` : 'Vestígios no Setor'
      case 'vestigios_usuario': return 'Meus Vestígios'
      case 'exames_atribuidos': return 'Meus Exames Atribuídos'
      case 'exames_solicitados_setor': return cell.sectorName ? `Exames Solicitados - ${cell.sectorName}` : 'Exames Solicitados ao Setor'
      default: return 'Painel de Informações'
    }
  }

  // Map avatar representation type
  const getKpiAvatarType = () => {
    if (cell.type?.includes('cadaveres')) return 'CADAVER'
    if (cell.type?.includes('exame')) return 'EXAME'
    return 'VESTIGIO'
  }

  return (
    <Grid item xs={12} sm={6} md={4}>
      <div className={classes.dashboardCell}>
        {/* Hover Settings trigger gear button */}
        <Tooltip title="Configurar KPI">
          <IconButton className={classes.configButton} onClick={onConfigClick}>
            <SettingsIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <div className={classes.kpiCard}>
          {/* Cell individual loading spinner overlay */}
          {isProcessing && (
            <div className={classes.cellLoadingOverlay}>
              <CircularProgress size={24} disableShrink />
            </div>
          )}

          <div className={classes.kpiHeader}>
            <div className={classes.kpiIconWrapper}>
              <ObjetoAvatar tipo={getKpiAvatarType()} />
            </div>
            <div className={classes.kpiInfo}>
              <Typography className={classes.kpiTitle}>{getKpiTitle()}</Typography>
              <Typography className={classes.kpiTotal}>{dataItem?.total || 0}</Typography>
            </div>
          </div>

          <Divider className={classes.kpiDivider} />

          {/* Render subcounters or lists depending on KPI type */}
          {isRegionalLayout ? (
            <div className={classes.kpiRegionalsContainer}>
              {dataItem?.regionais?.map((reg) => (
                <div key={reg.name} className={classes.regionalBadge}>
                  <Typography className={classes.regionalLabel}>
                    {reg.label === 'Pau dos Ferros' ? 'PDF' : reg.label}
                  </Typography>
                  <Typography className={classes.regionalValue}>{reg.value}</Typography>
                </div>
              ))}
            </div>
          ) : cell.type === 'exames_atribuidos' ? (
            <div className={classes.kpiRegionalsContainer}>
              <div className={classes.statusBadge} style={{ backgroundColor: 'rgba(2, 132, 199, 0.08)' }}>
                <Typography className={classes.statusLabel} style={{ color: '#0284C7' }}>Em Andamento</Typography>
                <Typography className={classes.statusValue} style={{ color: '#0284C7' }}>{dataItem?.emAndamento || 0}</Typography>
              </div>
              <div className={classes.statusBadge} style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)' }}>
                <Typography className={classes.statusLabel} style={{ color: '#10B981' }}>Finalizados</Typography>
                <Typography className={classes.statusValue} style={{ color: '#10B981' }}>{dataItem?.finalizados || 0}</Typography>
              </div>
            </div>
          ) : cell.type === 'exames_solicitados_setor' ? (
            <div className={classes.kpiRegionalsContainer}>
              <div className={classes.statusBadge} style={{ backgroundColor: 'rgba(244, 103, 44, 0.08)' }}>
                <Typography className={classes.statusLabel} style={{ color: '#F4672C' }}>Pendentes</Typography>
                <Typography className={classes.statusValue} style={{ color: '#F4672C' }}>{dataItem?.pendentes || 0}</Typography>
              </div>
              <div className={classes.statusBadge} style={{ backgroundColor: 'rgba(2, 132, 199, 0.08)' }}>
                <Typography className={classes.statusLabel} style={{ color: '#0284C7' }}>Em Andamento</Typography>
                <Typography className={classes.statusValue} style={{ color: '#0284C7' }}>{dataItem?.emAndamento || 0}</Typography>
              </div>
              <div className={classes.statusBadge} style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)' }}>
                <Typography className={classes.statusLabel} style={{ color: '#10B981' }}>Finalizados</Typography>
                <Typography className={classes.statusValue} style={{ color: '#10B981' }}>{dataItem?.finalizados || 0}</Typography>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 'auto', paddingTop: '4px' }}>
              <Typography variant="body2" style={{ color: '#888', fontStyle: 'italic', fontSize: '0.8rem' }}>
                {cell.type === 'vestigios_setor' ? 'Material unificado sob custódia do setor' : 'Material temporário em posse para análise'}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Grid>
  )
}

DashboardKpiCell.propTypes = {
  cell: PropTypes.object.isRequired,
  dataItem: PropTypes.object,
  isProcessing: PropTypes.bool,
  onConfigClick: PropTypes.func.isRequired,
}
